// src/utils/errorHandling.ts - Comprehensive error handling and retry mechanisms
import { logger } from './logger';

export interface RetryConfig {
  maxAttempts: number;
  baseDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
  jitter: boolean;
}

export interface ErrorContext {
  operation: string;
  weekKey?: string;
  sectionKey?: string;
  userId?: string;
  workspaceId?: string;
  timestamp: string;
  attempt?: number;
  maxAttempts?: number;
}

export interface ErrorReport {
  context: ErrorContext;
  error: Error;
  isRetryable: boolean;
  shouldRetry: boolean;
  nextRetryDelay?: number;
  userMessage: string;
  technicalMessage: string;
}

// --- DEFAULT CONFIGURATION ---------------------------------------------------

export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxAttempts: 3,
  baseDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
  backoffMultiplier: 2,
  jitter: true,
};

// --- ERROR CLASSIFICATION -----------------------------------------------------

/** Classify error type and determine if retryable */
export function classifyError(error: unknown): {
  type: 'network' | 'auth' | 'validation' | 'permission' | 'server' | 'unknown';
  isRetryable: boolean;
  userMessage: string;
  technicalMessage: string;
} {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    
    // Network errors (retryable)
    if (message.includes('network') || message.includes('fetch')) {
      return {
        type: 'network',
        isRetryable: true,
        userMessage: 'Network connection issue. Please check your internet connection.',
        technicalMessage: `Network error: ${error.message}`,
      };
    }
    
    // Timeout errors (retryable)
    if (message.includes('timeout')) {
      return {
        type: 'network',
        isRetryable: true,
        userMessage: 'Request timed out. Please try again.',
        technicalMessage: `Timeout error: ${error.message}`,
      };
    }
    
    // Authentication errors (not retryable)
    if (message.includes('auth') || message.includes('unauthorized') || message.includes('forbidden')) {
      return {
        type: 'auth',
        isRetryable: false,
        userMessage: 'Authentication required. Please log in again.',
        technicalMessage: `Auth error: ${error.message}`,
      };
    }
    
    // Permission errors (not retryable)
    if (message.includes('permission') || message.includes('access denied')) {
      return {
        type: 'permission',
        isRetryable: false,
        userMessage: 'You do not have permission to perform this action.',
        technicalMessage: `Permission error: ${error.message}`,
      };
    }
    
    // Validation errors (not retryable)
    if (message.includes('validation') || message.includes('invalid')) {
      return {
        type: 'validation',
        isRetryable: false,
        userMessage: 'Invalid data provided. Please check your input.',
        technicalMessage: `Validation error: ${error.message}`,
      };
    }
    
    // Server errors (retryable)
    if (message.includes('500') || message.includes('502') || message.includes('503') || message.includes('504')) {
      return {
        type: 'server',
        isRetryable: true,
        userMessage: 'Server error occurred. Please try again.',
        technicalMessage: `Server error: ${error.message}`,
      };
    }
    
    // Rate limiting (retryable with backoff)
    if (message.includes('rate limit') || message.includes('too many requests')) {
      return {
        type: 'server',
        isRetryable: true,
        userMessage: 'Too many requests. Please wait a moment and try again.',
        technicalMessage: `Rate limit error: ${error.message}`,
      };
    }
  }
  
  // Unknown error
  return {
    type: 'unknown',
    isRetryable: false,
    userMessage: 'An unexpected error occurred. Please try again.',
    technicalMessage: `Unknown error: ${String(error)}`,
  };
}

// --- RETRY MECHANISM ---------------------------------------------------------

/** Calculate retry delay with exponential backoff and jitter */
export function calculateRetryDelay(
  attempt: number,
  config: RetryConfig = DEFAULT_RETRY_CONFIG
): number {
  const exponentialDelay = config.baseDelay * Math.pow(config.backoffMultiplier, attempt - 1);
  const cappedDelay = Math.min(exponentialDelay, config.maxDelay);
  
  if (config.jitter) {
    // Add random jitter to prevent thundering herd
    const jitterRange = cappedDelay * 0.1; // 10% jitter
    const jitter = (Math.random() - 0.5) * 2 * jitterRange;
    return Math.max(0, cappedDelay + jitter);
  }
  
  return cappedDelay;
}

/** Create error report with context */
export function createErrorReport(
  error: unknown,
  context: ErrorContext,
  attempt: number = 1,
  config: RetryConfig = DEFAULT_RETRY_CONFIG
): ErrorReport {
  const classification = classifyError(error);
  const shouldRetry = classification.isRetryable && attempt < config.maxAttempts;
  const nextRetryDelay = shouldRetry ? calculateRetryDelay(attempt, config) : undefined;
  
  return {
    context: {
      ...context,
      attempt,
      maxAttempts: config.maxAttempts,
    },
    error: error instanceof Error ? error : new Error(String(error)),
    isRetryable: classification.isRetryable,
    shouldRetry,
    nextRetryDelay,
    userMessage: classification.userMessage,
    technicalMessage: classification.technicalMessage,
  };
}

// --- RETRY WRAPPER ------------------------------------------------------------
// NOTE: Retry functionality is provided by retryUtils.ts (withRetry, withRetryAndTimeout)
// This file focuses on error classification, reporting, and monitoring
// Remove duplicate withRetry - using retryUtils.ts instead

// Removed unused exports in Phase 4 Batch 2:
// - createErrorBoundaryContext
// - handleComponentError
// (function bodies removed - were unused)

// --- GLOBAL ERROR HANDLING ---------------------------------------------------

/** Global error handler for unhandled promises */
export function setupGlobalErrorHandling(): void {
  if (typeof window === 'undefined') return;
  
  // Enhanced unhandled rejection handler
  const handleUnhandledRejection = async (event: PromiseRejectionEvent) => {
    const error = event.reason instanceof Error 
      ? event.reason 
      : new Error(String(event.reason));
    
    // Enhanced logging with stack trace and context
    logger.error('Unhandled promise rejection', {
      reason: event.reason,
      error: error.message,
      stack: error.stack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    }, 'GlobalErrorHandler');
    
    // ✅ STEP 2.4: Sentry error tracking integration
    if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
      try {
        const Sentry = await import('@sentry/react');
        Sentry.captureException(error, { 
          tags: { source: 'unhandledRejection' },
          contexts: {
            browser: {
              name: navigator.userAgent,
              url: window.location.href,
            }
          }
        });
      } catch (sentryError) {
        // Ignore Sentry errors - don't break error handling
        if (import.meta.env.DEV) {
          logger.debug('Sentry captureException failed', { error: sentryError }, 'GlobalErrorHandler');
        }
      }
    }
    
    // Prevent default browser error handling
    event.preventDefault();
  };
  
  window.addEventListener('unhandledrejection', handleUnhandledRejection);
  
  // Enhanced uncaught error handler
  const handleError = async (event: ErrorEvent) => {
    logger.error('Uncaught error', {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error,
      stack: event.error?.stack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    }, 'GlobalErrorHandler');
    
    // ✅ STEP 2.4: Sentry error tracking integration
    if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN && event.error) {
      try {
        const Sentry = await import('@sentry/react');
        Sentry.captureException(event.error, { 
          tags: { source: 'uncaughtError' },
          contexts: {
            browser: {
              name: navigator.userAgent,
              url: window.location.href,
              filename: event.filename,
              lineno: event.lineno,
              colno: event.colno,
            }
          }
        });
      } catch (sentryError) {
        // Ignore Sentry errors - don't break error handling
        if (import.meta.env.DEV) {
          logger.debug('Sentry captureException failed', { error: sentryError }, 'GlobalErrorHandler');
        }
      }
    }
  };
  
  window.addEventListener('error', handleError);
  
  // Verify handlers are registered (dev only)
  if (import.meta.env.DEV) {
    logger.debug('Global error handlers registered', undefined, 'GlobalErrorHandler');
  }
}

// Removed unused exports in Phase 4 Batch 2:
// - isRecoverableError
// - getUserFriendlyMessage
// - getTechnicalMessage
// (function bodies removed - were unused)

// Removed unused exports in Phase 4 Batch 2:
// - ErrorMonitor interface
// - consoleErrorMonitor
// - noopErrorMonitor
// (function bodies removed - were unused)

// Removed unused exports in Phase 4 Batch 2:
// - createSafeError
// - isErrorType
// - extractErrorMessage
// (function bodies removed - were unused)
